const ABP_DISPLAY_TIME_ZONE = 'America/Los_Angeles'

const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/

const CALENDAR_MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

type CalendarDateParts = {
  year: number
  month: number
  day: number
}

function parseCalendarDateParts(value: string): CalendarDateParts | null {
  const match = value.trim().match(CALENDAR_DATE_PATTERN)
  if (!match) {
    return null
  }

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  }
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const numberFormatter = new Intl.NumberFormat('en-US')

function formatCurrency(amount: number): string {
  return currencyFormatter.format(amount)
}

function formatCalendarDate(value: string): string {
  const parts = parseCalendarDateParts(value)
  if (!parts) {
    return formatDate(value)
  }

  const month = String(parts.month).padStart(2, '0')
  const day = String(parts.day).padStart(2, '0')
  return `${month}/${day}/${parts.year}`
}

function formatCalendarDateLongMonth(value: string): string {
  const parts = parseCalendarDateParts(value)
  if (!parts) {
    return formatDateLongMonth(value)
  }

  const monthName = CALENDAR_MONTH_NAMES[parts.month - 1]
  return `${monthName} ${parts.day}, ${parts.year}`
}

function getCalendarYear(value: string): number | null {
  const parts = parseCalendarDateParts(value)
  if (!parts) {
    return null
  }

  return parts.year
}

function formatCalendarDateRangeNumeric(start: string, end: string): string {
  return `${formatCalendarDate(start)} - ${formatCalendarDate(end)}`
}

function formatDate(iso: string): string {
  const calendarDate = parseCalendarDateParts(iso)
  if (calendarDate) {
    return formatCalendarDate(iso)
  }

  const date = new Date(iso)
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: ABP_DISPLAY_TIME_ZONE,
  })
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

// Year of an ISO date in the ABP display time zone, so it agrees with the
// dates formatted above (avoids a browser-local-vs-Pacific year mismatch).
// A date-only string (e.g. a benefit effective date "2027-01-01") is a calendar
// date, not an instant: read its year as written. new Date() would parse it as
// UTC midnight, which is still Dec 31 of the previous year in Pacific time.
function getYear(iso: string): number {
  if (DATE_ONLY_PATTERN.test(iso.trim())) {
    return Number(iso.trim().slice(0, 4))
  }

  const date = new Date(iso)
  return Number(
    date.toLocaleDateString('en-US', {
      year: 'numeric',
      timeZone: ABP_DISPLAY_TIME_ZONE,
    })
  )
}

function formatDateLong(iso: string): string {
  const calendarDate = parseCalendarDateParts(iso)
  if (calendarDate) {
    const monthName = CALENDAR_MONTH_NAMES[calendarDate.month - 1].slice(0, 3)
    return `${monthName} ${calendarDate.day}, ${calendarDate.year}`
  }
  const date = new Date(iso)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: ABP_DISPLAY_TIME_ZONE,
  })
}

function formatDateLongMonth(iso: string): string {
  const calendarDate = parseCalendarDateParts(iso)
  if (calendarDate) {
    return formatCalendarDateLongMonth(iso)
  }

  const date = new Date(iso)
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: ABP_DISPLAY_TIME_ZONE,
  })
}

function formatDateRange(startIso: string, endIso: string): string {
  // Calendar-date strings (YYYY-MM-DD) must not be passed through new Date()
  // because JS parses them as UTC midnight, which shifts by one day when
  // displayed in America/Los_Angeles (UTC-8 in winter). Extract parts directly.
  const startParts = parseCalendarDateParts(startIso)
  const endParts = parseCalendarDateParts(endIso)

  if (startParts && endParts) {
    const startMonth = CALENDAR_MONTH_NAMES[startParts.month - 1].slice(0, 3)
    const endMonth = CALENDAR_MONTH_NAMES[endParts.month - 1].slice(0, 3)
    return `${startMonth} ${startParts.day} - ${endMonth} ${endParts.day}, ${endParts.year}`
  }

  const start = new Date(startIso)
  const end = new Date(endIso)
  const startStr = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: ABP_DISPLAY_TIME_ZONE,
  })
  const endStr = end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: ABP_DISPLAY_TIME_ZONE,
  })
  return `${startStr} - ${endStr}`
}

function formatDateRangeNumeric(startIso: string, endIso: string): string {
  return `${formatDate(startIso)} – ${formatDate(endIso)}`
}

// Same long-month style as formatDateLongMonth (e.g. "January 1, 2027"),
// applied to a start/end pair (e.g. "January 1 - February 1, 2027").
function formatDateRangeLongMonth(startIso: string, endIso: string): string {
  const startParts = parseCalendarDateParts(startIso)
  const endParts = parseCalendarDateParts(endIso)

  if (startParts && endParts) {
    const startMonth = CALENDAR_MONTH_NAMES[startParts.month - 1]
    const endMonth = CALENDAR_MONTH_NAMES[endParts.month - 1]
    return `${startMonth} ${startParts.day} - ${endMonth} ${endParts.day}, ${endParts.year}`
  }

  const start = new Date(startIso)
  const end = new Date(endIso)
  const startStr = start.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: ABP_DISPLAY_TIME_ZONE,
  })
  const endStr = end.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: ABP_DISPLAY_TIME_ZONE,
  })
  return `${startStr} - ${endStr}`
}

function getDateTimeFormatPart(
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes
): string {
  return parts.find((part) => part.type === type)?.value ?? ''
}

function formatDateTime(iso: string): string {
  const date = new Date(iso)
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ABP_DISPLAY_TIME_ZONE,
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date)

  const month = getDateTimeFormatPart(parts, 'month')
  const day = getDateTimeFormatPart(parts, 'day')
  const year = getDateTimeFormatPart(parts, 'year')
  const hour = getDateTimeFormatPart(parts, 'hour')
  const minute = getDateTimeFormatPart(parts, 'minute')
  const meridiem = getDateTimeFormatPart(parts, 'dayPeriod').toUpperCase()

  return `${month}/${day}/${year} ${hour}:${minute} ${meridiem}`
}

function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

function formatPercentageRaw(value: number): string {
  const roundedToTenth = Math.round(value * 10) / 10
  const hasTenthFraction = Math.abs(roundedToTenth % 1) > 1e-9
  if (!hasTenthFraction) {
    return `${Math.round(roundedToTenth)}%`
  }
  return `${roundedToTenth.toFixed(1)}%`
}

function formatNumber(n: number): string {
  return numberFormatter.format(n)
}

function durationInDays(startIso: string, endIso: string): number {
  const start = new Date(startIso)
  const end = new Date(endIso)
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
}

export const OEPFormatters = {
  formatCurrency,
  formatCalendarDate,
  formatCalendarDateLongMonth,
  formatCalendarDateRangeNumeric,
  getCalendarYear,
  formatDate,
  getYear,
  formatDateLong,
  formatDateLongMonth,
  formatDateRange,
  formatDateRangeNumeric,
  formatDateRangeLongMonth,
  formatDateTime,
  formatPercentage,
  formatPercentageRaw,
  formatNumber,
  durationInDays,
}
